import React, { useState, useEffect } from 'react';
import { Card, Box, CardHeader, TextField, CardContent, Divider, Grid, Typography, IconButton, Button, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AddBlogPostPage = () => {
  const [newPostData, setNewPostData] = useState({ title: '', bu_master: '', description: '', category: '', author: '', image: '' });
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [buMasters, setBuMasters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const getUserDetailsFromLocalStorage = () => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      return { username: decodedToken.username };
    }
    return { username: '' };
  };

  useEffect(() => {
    const { username } = getUserDetailsFromLocalStorage();
    setNewPostData((prevData) => ({
      ...prevData,
      author: username || '',
    }));

    fetchBuMasters();
  }, []);

  const fetchBuMasters = async () => {
    try {
      const response = await axios.get('https://3wzg6m6x-5000.asse.devtunnels.ms/api/bu_master');
      setBuMasters(response.data);
    } catch (error) {
      console.error('Error fetching BU Masters:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newPostData.title) newErrors.title = 'Title is required';
    if (!newPostData.bu_master) newErrors.bu_master = 'BU Master is required';
    if (!newPostData.category) newErrors.category = 'Category is required';
    if (!newPostData.description) newErrors.description = 'Description is required';
    if (!newPostData.editor) newErrors.editor = 'Editor Pick is required';
    if (!imageFile) newErrors.image = 'Image is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddPost = async () => {
    if (!validateForm()) return;

    setLoading(true); 
    const formDatas = new FormData();
    formDatas.append('author', newPostData.author);
    formDatas.append('editor', newPostData.editor);
    formDatas.append('id_bu_master', newPostData.bu_master);
    formDatas.append('description', newPostData.description);
    formDatas.append('category', newPostData.category);
    formDatas.append('image', imageFile);
    formDatas.append('title', newPostData.title);

    try {
      const token = localStorage.getItem('token');
      await axios.post('https://3wzg6m6x-5000.asse.devtunnels.ms/api/blog/add', formDatas, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Blog post added successfully!',
      });

      navigate('/compro/blog-post'); 
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add blog post!',
      });
    } finally {
      setLoading(false); 
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 3, padding: 3 }}>
      <CardHeader
        title={<Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 'bold' }}>Add Blog Post</Typography>}
        sx={{ paddingBottom: 2 }}
      />
      <Divider />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Title"
              value={newPostData.title}
              onChange={(e) => setNewPostData({ ...newPostData, title: e.target.value })}
              variant="outlined"
              sx={{ borderRadius: 2 }}
              error={!!errors.title}
              helperText={errors.title}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" sx={{ borderRadius: 2 }} error={!!errors.bu_master}>
              <InputLabel>BU Master</InputLabel>
              <Select
                value={newPostData.bu_master}
                onChange={(e) => setNewPostData({ ...newPostData, bu_master: e.target.value })}
                label="BU Master"
              >
                {buMasters.map((bu) => (
                  <MenuItem key={bu.id} value={bu.id}>{bu.name}</MenuItem>
                ))}
              </Select>
              {errors.bu_master && <Typography color="error">{errors.bu_master}</Typography>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <ReactQuill
              value={newPostData.description}
              onChange={(value) => setNewPostData({ ...newPostData, description: value })}
              modules={{
                toolbar: [
                  [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  ['bold', 'italic', 'underline'],
                  ['link'],
                  [{ 'align': [] }],
                  ['image'],
                ],
              }}
              style={{ height: '150px', width: '100%' }}
            />
            {errors.description && <Typography color="error">{errors.description}</Typography>}
          </Grid>

          <Grid item xs={12} md={6} style={{ marginTop: '56px' }}>
            <TextField
              fullWidth
              label="Category"
              value={newPostData.category}
              onChange={(e) => setNewPostData({ ...newPostData, category: e.target.value })}
              variant="outlined"
              sx={{ borderRadius: 2 }}
              error={!!errors.category}
              helperText={errors.category}
            />
          </Grid>

          <Grid item xs={12} md={6} style={{ marginTop: '40px' }}>
            <TextField
              label="Author"
              variant="outlined"
              fullWidth
              margin="normal"
              value={newPostData.author}
              InputProps={{
                readOnly: true,
              }}
              sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRadius: 2 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" sx={{ borderRadius: 2 }} error={!!errors.editor}>
              <InputLabel>Editor Pick</InputLabel>
              <Select
                value={newPostData.editor}
                onChange={(e) => setNewPostData({ ...newPostData, editor: e.target.value })}
                label="Editor Pick"
              >
                <MenuItem value={0}>No</MenuItem>
                <MenuItem value={1}>Yes</MenuItem>
              </Select>
              {errors.editor && <Typography color="error">{errors.editor}</Typography>}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <input type="file" onChange={handleImageChange} style={{ display: 'none' }} id="image-upload" />
            <label htmlFor="image-upload">
              <Button variant="outlined" fullWidth component="span" sx={{ borderRadius: 2, boxShadow: 2 }}>
                Upload Image
              </Button>
            </label>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  marginTop: '16px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                }}
              />
            )}
            {errors.image && <Typography color="error">{errors.image}</Typography>}
          </Grid>

          <Grid item xs={12} sx={{ marginTop: 3 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleAddPost}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Post'}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AddBlogPostPage;
